import { type QueryKey, useQueries, type UseQueryOptions } from '@tanstack/react-query';

import type { Permission } from '@/api/permissions';

import type { GetRoleRes, RoleSummary } from './types';

export type RolePermissionSets = {
  sets: ReadonlyMap<string, ReadonlySet<Permission>>;
  /** True once any role's detail has failed; the comparison then offers a retry, not a spinner. */
  isError: boolean;
  retry: () => void;
};

/**
 * Every listed role's permission set, read from each role's own detail. The list response
 * carries no permissions, and the board needs them for the custom-role subtitles and the
 * comparison matrix. A role whose detail has not landed yet is simply absent from the map.
 */
export const useRolePermissionSets = <TKey extends QueryKey>(
  roles: readonly RoleSummary[],
  detailQuery: (_roleId: string) => UseQueryOptions<GetRoleRes, Error, GetRoleRes, TKey>,
): RolePermissionSets =>
  useQueries({
    queries: roles.map((role) => detailQuery(role.id)),
    combine: (results) => ({
      sets: new Map(
        results.flatMap(({ data }) =>
          data
            ? [[data.id, new Set(data.permissions.map(({ permission }) => permission))] as const]
            : [],
        ),
      ),
      isError: results.some((result) => result.isError),
      retry: () => {
        for (const result of results) {
          if (result.isError) {
            void result.refetch();
          }
        }
      },
    }),
  });
